import React, { useState, useEffect } from 'react';
import { formsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Loader2, Download, Search, FileText, ExternalLink, Sparkles, Filter, CheckCircle, AlertCircle, BarChart3, Send, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useNavigate } from 'react-router-dom';
import { FormChecklist } from '../components/FormChecklist';

const CATS = [
  'Family-Based', 'Employment-Based', 'Green Card', 'Work Authorization',
  'Travel & Parole', 'Naturalization', 'Asylum & Refugee', 'Humanitarian',
  'Waivers', 'Medical', 'Employment', 'Administrative', 'Consular',
];

function matchesSearch(form, term) {
  if (!term) return true;
  var q = term.toLowerCase();
  var nameMatch = form.name.toLowerCase().indexOf(q) >= 0;
  var idMatch = form.id.toLowerCase().indexOf(q) >= 0;
  return nameMatch || idMatch;
}

function filterForms(forms, search, category, autoOnly) {
  return forms.filter(function(f) {
    if (!matchesSearch(f, search)) return false;
    if (category !== 'all' && f.category !== category) return false;
    if (autoOnly && !f.auto_fill) return false;
    return true;
  });
}

function groupByCategory(list) {
  var result = {};
  list.forEach(function(f) {
    if (!result[f.category]) result[f.category] = [];
    result[f.category].push(f);
  });
  return result;
}

function getScoreColorClass(pct) {
  if (pct >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (pct >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-500 bg-red-50 border-red-200';
}
function getBarColorClass(pct) {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-400';
}

function FormCard({ form, onAutoFill }) {
  var borderClass = form.auto_fill ? 'border-l-4 border-l-primary' : '';
  return (
    <Card className={borderClass + ' hover:border-primary/40 transition-all'} data-testid={'form-card-' + form.id}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-heading text-sm font-bold text-primary">{form.id}</span>
          {form.auto_fill ? <Badge className="bg-primary/10 text-primary text-[10px]"><Sparkles className="w-2.5 h-2.5 mr-0.5" /> Auto-Fill</Badge> : null}
        </div>
        <p className="font-body text-sm font-medium text-foreground mb-1 line-clamp-1">{form.name}</p>
        <p className="font-body text-xs text-muted-foreground mb-3 line-clamp-2">{form.description}</p>
        {form.auto_fill && form.scoreData ? (
          <div className="mb-2" data-testid={'score-' + form.id}>
            <div className="flex items-center justify-between mb-1">
              <span className={'font-body text-[10px] font-semibold px-1.5 py-0.5 rounded border ' + getScoreColorClass(form.scoreData.score)}>{form.scoreData.score}% fillable</span>
              <span className="font-body text-[10px] text-muted-foreground">{form.scoreData.filled}/{form.scoreData.total} fields</span>
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div className={'h-full rounded-full transition-all ' + getBarColorClass(form.scoreData.score)} style={{width: form.scoreData.score + '%'}} />
            </div>
          </div>
        ) : null}
        {form.auto_fill ? (
          <Button size="sm" className="bg-primary text-white font-body text-xs w-full" onClick={function() { onAutoFill(form.id); }} data-testid={'auto-fill-' + form.id}>
            <Download className="w-3 h-3 mr-1" /> Fill and Download
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="font-body text-xs w-full" onClick={function() { window.open(form.uscis_url, '_blank'); }} data-testid={'download-' + form.id}>
            <ExternalLink className="w-3 h-3 mr-1" /> Get from USCIS
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function CategoryGroup({ cat, items, onAutoFill }) {
  return (
    <div>
      <h2 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        {cat} <Badge variant="secondary" className="text-xs font-body">{items.length}</Badge>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map(function(f) { return <FormCard key={f.id} form={f} onAutoFill={onAutoFill} />; })}
      </div>
    </div>
  );
}

export default function FormFillPage() {
  var _state = useState([]);
  var forms = _state[0], setForms = _state[1];
  var _state2 = useState([]);
  var cases = _state2[0], setCases = _state2[1];
  var _state3 = useState(true);
  var loading = _state3[0], setLoading = _state3[1];
  var _state4 = useState(false);
  var generating = _state4[0], setGenerating = _state4[1];
  var _state5 = useState('');
  var selectedCase = _state5[0], setSelectedCase = _state5[1];
  var _state6 = useState('');
  var selectedForm = _state6[0], setSelectedForm = _state6[1];
  var _state7 = useState('');
  var searchTerm = _state7[0], setSearchTerm = _state7[1];
  var _state8 = useState('all');
  var categoryFilter = _state8[0], setCategoryFilter = _state8[1];
  var _state9 = useState(false);
  var showAutoFillOnly = _state9[0], setShowAutoFillOnly = _state9[1];
  var _state10 = useState(false);
  var fillDialog = _state10[0], setFillDialog = _state10[1];
  var _state11 = useState(0);
  var autoFillCount = _state11[0], setAutoFillCount = _state11[1];
  var _stateScores = useState({});
  var completionScores = _stateScores[0], setCompletionScores = _stateScores[1];
  var _stateSC = useState('');
  var scoreCaseId = _stateSC[0], setScoreCaseId = _stateSC[1];
  var _stateSL = useState(false);
  var scoresLoading = _stateSL[0], setScoresLoading = _stateSL[1];
  var _stateRL = useState(null);
  var requestLink = _stateRL[0], setRequestLink = _stateRL[1];
  var _stateRQ = useState(false);
  var requesting = _stateRQ[0], setRequesting = _stateRQ[1];
  var _stateCL = useState(null);
  var checklist = _stateCL[0], setChecklist = _stateCL[1];
  var _stateCLL = useState(false);
  var checklistLoading = _stateCLL[0], setChecklistLoading = _stateCLL[1];
  var navigate = useNavigate();

  useEffect(function() {
    Promise.all([formsAPI.getAvailable(), casesAPI.getAll()])
      .then(function(results) {
        setForms(results[0].data.forms);
        setCases(results[1].data);
        setAutoFillCount(results[0].data.auto_fill_count || 0);
      })
      .catch(function() { toast.error('Failed to load data'); })
      .finally(function() { setLoading(false); });
  }, []);

  function loadCompletionScores(cid) {
    if (!cid || cid === 'none') { setCompletionScores({}); setScoreCaseId(''); return; }
    setScoresLoading(true);
    setScoreCaseId(cid);
    formsAPI.getCompletionScores(cid)
      .then(function(res) { setCompletionScores(res.data); })
      .catch(function() { toast.error('Failed to load scores'); })
      .finally(function() { setScoresLoading(false); });
  }

  function loadChecklist(formId, caseId) {
    if (!formId || !caseId || caseId === 'none') { setChecklist(null); return; }
    setChecklistLoading(true);
    formsAPI.getChecklist(formId, caseId)
      .then(function(res) { setChecklist(res.data); })
      .catch(function() { setChecklist(null); })
      .finally(function() { setChecklistLoading(false); });
  }

  var filtered = filterForms(forms, searchTerm, categoryFilter, showAutoFillOnly);
  var enrichedForms = [];
  for (var eIdx = 0; eIdx < filtered.length; eIdx++) {
    var ef = filtered[eIdx];
    var esd = completionScores[ef.id] || null;
    enrichedForms.push(Object.assign({}, ef, { scoreData: esd }));
  }
  var grouped = groupByCategory(enrichedForms);
  var categories = [];
  var seen = {};
  forms.forEach(function(f) { if (!seen[f.category]) { seen[f.category] = true; categories.push(f.category); } });
  var sortedCats = CATS.filter(function(c) { return grouped[c]; });

  function openAutoFill(formId) {
    setSelectedForm(formId);
    setRequestLink(null);
    setChecklist(null);
    setFillDialog(true);
    // Load checklist if a case is already selected
    if (selectedCase && selectedCase !== 'none') {
      loadChecklist(formId, selectedCase);
    }
  }

  function handleRequestData(sendEmail) {
    if (!selectedCase || !selectedForm) return;
    setRequesting(true);
    formsAPI.requestData(selectedForm, selectedCase, sendEmail)
      .then(function(res) {
        var data = res.data;
        if (data.link) {
          setRequestLink(data);
          if (sendEmail && data.email_sent) {
            toast.success('Email sent with collection link!');
          } else if (sendEmail && !data.email_sent) {
            toast.info('Link created but email could not be sent. Copy the link below.');
          }
        } else {
          toast.success(data.message || 'No missing fields!');
        }
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to create request');
      })
      .finally(function() { setRequesting(false); });
  }

  function copyLink() {
    if (requestLink && requestLink.link) {
      navigator.clipboard.writeText(requestLink.link);
      toast.success('Link copied to clipboard!');
    }
  }

  function onDialogCaseSelect(cid) {
    setSelectedCase(cid);
    if (cid) {
      loadCompletionScores(cid);
      if (selectedForm) loadChecklist(selectedForm, cid);
    } else {
      setChecklist(null);
    }
  }

  function handleGenerate() {
    if (!selectedCase || !selectedForm) return;
    setGenerating(true);
    formsAPI.generateFilled(selectedForm, selectedCase)
      .then(function(res) {
        var blob = new Blob([res.data], { type: 'application/pdf' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = selectedForm + '_filled.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Form generated!');
        setFillDialog(false);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to generate');
      })
      .finally(function() { setGenerating(false); });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="ml-0 md:ml-64 pt-16 md:pt-0 p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">Immigration Forms Library</h1>
          <p className="font-body text-muted-foreground mt-1">{forms.length} forms available - {autoFillCount} with auto-fill</p>
        </div>

        <Card className="mb-6" data-testid="forms-filter-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="font-body text-xs text-muted-foreground mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search forms..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="pl-9 font-body text-sm h-9" data-testid="form-search" />
                </div>
              </div>
              <div className="min-w-[180px]">
                <Label className="font-body text-xs text-muted-foreground mb-1 block">Category</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="font-body text-sm h-9" data-testid="category-filter">
                    <Filter className="w-3 h-3 mr-2" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(function(c) { return <SelectItem key={c} value={c}>{c}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
              <Button variant={showAutoFillOnly ? 'default' : 'outline'} size="sm" className="h-9 font-body text-sm" onClick={function() { setShowAutoFillOnly(!showAutoFillOnly); }} data-testid="auto-fill-filter">
                <Sparkles className="w-3 h-3 mr-1.5" /> Auto-Fill Only
              </Button>
              <div className="min-w-[200px]">
                <Label className="font-body text-xs text-muted-foreground mb-1 block">Completion Scores</Label>
                <Select value={scoreCaseId} onValueChange={loadCompletionScores}>
                  <SelectTrigger className="font-body text-sm h-9" data-testid="score-case-select">
                    <BarChart3 className="w-3 h-3 mr-2" /><SelectValue placeholder="Select case..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No case selected</SelectItem>
                    {cases.map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="font-body text-sm text-muted-foreground mb-4" data-testid="results-count">Showing {filtered.length} of {forms.length} forms</p>
        {scoresLoading ? <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading completion scores...</div> : null}

        {filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-body text-muted-foreground">No forms match your search</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {sortedCats.map(function(cat) {
              return <CategoryGroup key={cat} cat={cat} items={grouped[cat]} onAutoFill={openAutoFill} />;
            })}
          </div>
        )}
      </main>

      <Dialog open={fillDialog} onOpenChange={setFillDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Auto-Fill {selectedForm}</DialogTitle>
            <DialogDescription className="font-body">Select a case to populate the form</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body">Select Case</Label>
              <Select value={selectedCase} onValueChange={onDialogCaseSelect}>
                <SelectTrigger className="font-body" data-testid="fill-case-select"><SelectValue placeholder="Choose a case..." /></SelectTrigger>
                <SelectContent>
                  {cases.map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <FormChecklist
              scoreObj={completionScores[selectedForm]}
              hasCase={!!selectedCase}
              checklist={checklist}
              checklistLoading={checklistLoading}
              onNavigateToTab={function(tab) {
                setFillDialog(false);
                var tabMap = { 'Personal': 0, 'Contact': 1, 'Immigration': 2, 'Biographic': 3, 'Employment & Education': 4, 'Family': 5 };
                var tabIdx = tabMap[tab] || 0;
                navigate('/cases/' + selectedCase + '?tab=' + tabIdx + '&edit=true');
              }}
            />
            {requestLink ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="request-link-result">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-body text-xs font-semibold text-emerald-700">Collection link created ({requestLink.missing_count} fields)</span>
                </div>
                <div className="flex gap-2">
                  <Input value={requestLink.link} readOnly className="text-xs h-8 font-mono" data-testid="request-link-input" />
                  <Button size="sm" variant="outline" onClick={copyLink} className="h-8 px-2" data-testid="copy-link-btn"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
                <p className="font-body text-[10px] text-muted-foreground">Expires: {requestLink.expires_at ? requestLink.expires_at.substring(0, 10) : '7 days'}</p>
              </div>
            ) : null}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={function() { setFillDialog(false); }} className="font-body">Cancel</Button>
            {selectedCase && !requestLink ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={function() { handleRequestData(false); }} disabled={requesting || !selectedCase} className="font-body text-xs" data-testid="request-link-only-btn">
                  {requesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />} Get Link
                </Button>
                <Button variant="outline" onClick={function() { handleRequestData(true); }} disabled={requesting || !selectedCase} className="font-body text-xs" data-testid="request-email-btn">
                  {requesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />} Email Client
                </Button>
              </div>
            ) : null}
            <Button onClick={handleGenerate} disabled={generating || !selectedCase} className="bg-primary text-white font-body" data-testid="generate-form-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
